import React, {
  useEffect,
  useState,
  useCallback,
  useReducer,
  ReactElement,
  useRef,
  Fragment,
} from "react";
import {
  Loading,
  useVersion,
  Title,
  useTranslate,
  Translate,
} from "react-admin";
import { Odometer, VehicleDto } from "../../../common/data/service-proxies";
import { GoogleMap } from "../GoogleMap";
import { GoogleMapMarker } from "../GoogleMapMarker";
import { ControlPosition, GoogleMapControls, LiveTrackEvent } from "../types";
import { Paper, Theme, ThemeProvider, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { LocationLiveTrackTitle } from "./LocationLiveTrackTitle";
import { AnonymousLocationLiveTrack } from "./AnonymousLocationLiveTrack";
import { AuthenticatedLocationLiveTrack } from "./AuthenticatedLocationLiveTrack";
import { parseISO } from "date-fns";

interface Props {
  id?: string;
  tenantId?: string;
}

interface InfoWindowProps {
  driverFullName?: string | null;
  drivingSpeed?: number | null;
  drivingSpeedUnit?: string | null;
  trailers?: string | null;
  location?: string | null;
  theme: Theme;
  translate: Translate;
}

const isNumber = (input: any): input is number => {
  return !isNaN(input);
};

const isNumberNullOrUndefined = (value?: number | null) => {
  return !value && value !== 0;
};

type UPDATE_LOCATION = "UPDATE_LOCATION";

interface LiveTrackEventAction {
  type: UPDATE_LOCATION;
  payload: LiveTrackEvent;
}

const createUpdateLocationAction = (
  payload: LiveTrackEvent
): LiveTrackEventAction => ({ type: "UPDATE_LOCATION", payload });

const reducer = (
  previousValue: LiveTrackEvent = {},
  action: LiveTrackEventAction
) => {
  if (action.type !== "UPDATE_LOCATION") {
    return previousValue;
  }

  if (previousValue.timestamp && action.payload.timestamp) {
    const previousTimestamp = parseISO(previousValue.timestamp);
    const currentTimestamp = parseISO(action.payload.timestamp);
    if (previousTimestamp > currentTimestamp) {
      // The incoming event is older than the previous one
      return previousValue;
    }
  }

  return {
    ...previousValue,
    ...action.payload,
  };
};

const InfoWindow = ({
  location,
  driverFullName,
  drivingSpeed,
  drivingSpeedUnit,
  trailers,
  theme,
  translate,
}: InfoWindowProps) => {
  return (
    <div className="info-window-content">
      <ThemeProvider theme={theme}>
        <Paper
          className="info-window-content-paper"
          style={{ backgroundColor: theme.palette.background.paper }}>
          <div className="info-window-content-group">
            <div>
              <Typography variant="body2">
                {translate("resources.locations-current.labels.driverName")}
              </Typography>
            </div>
            <div>
              <Typography variant="body2">{driverFullName}</Typography>
            </div>
          </div>
          <div className="info-window-content-group">
            <div>
              <Typography variant="body2">Location</Typography>
            </div>
            <div>
              <Typography variant="body2">{location}</Typography>
            </div>
          </div>
          <div className="info-window-content-group">
            <div>
              <Typography variant="body2">
                {translate("resources.locations-current.labels.trailers")}
              </Typography>
            </div>
            <div>
              <Typography variant="body2">
                {trailers ??
                  translate("resources.locations-current.labels.none")}
              </Typography>
            </div>
          </div>
          <div className="info-window-content-group">
            <div>
              <Typography variant="body2">
                {translate("resources.locations-current.labels.drivingSpeed")}
              </Typography>
            </div>
            <div>
              <Typography variant="body2">
                {drivingSpeed ?? "-"} {drivingSpeedUnit}
              </Typography>
            </div>
          </div>
        </Paper>
      </ThemeProvider>
    </div>
  );
};

const getMapControls = (
  props: InfoWindowProps
): {
  controls: { [name: string]: ReactElement };
  controlPositions: { [name: string]: ControlPosition };
} => {
  return {
    controls: {
      test: <InfoWindow {...props} />,
    },
    controlPositions: { test: "TOP_CENTER" },
  };
};

export const LocationLiveTrack = ({ id, tenantId }: Props) => {
  const translate = useTranslate();
  const theme: Theme = useTheme();
  const version = useVersion();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [location, dispatch] = useReducer(reducer, {});

  const [vehicle, setVehicle] = useState<VehicleDto>();
  const [lastResponse, setLastResponse] = useState<Date>();

  const mapRef = useRef<GoogleMapControls>(null);

  const handleLocationUpdated = useCallback((args: LiveTrackEvent) => {
    if (
      typeof args.lat === "undefined" ||
      typeof args.lng === "undefined" ||
      typeof args.bearing === "undefined" ||
      args.lat === null ||
      args.lng === null ||
      args.bearing === null
    ) {
      return;
    }

    setLastResponse(new Date());
    setError(undefined);
    dispatch(
      createUpdateLocationAction({
        lat: args.lat,
        lng: args.lng,
        bearing: args.bearing,
        driverName: args.driverName,
        speed: args.speed,
        trailers: args.trailers,
        geolocation: args.geolocation,
        timestamp: args.timestamp,
      })
    );
  }, []);

  useEffect(() => {
    setError(undefined);
    setLoading(true);
  }, [version]);

  useEffect(() => {
    if (error) {
      return;
    }

    setLoading(
      isNumberNullOrUndefined(location.bearing) ||
        isNumberNullOrUndefined(location.lat) ||
        isNumberNullOrUndefined(location.lng)
    );
    const timeout = setTimeout(() => {
      setLoading(false);
      setError(
        "The vehicle might not have data coverage, please hold on a little more or try again later"
      );
    }, 60000);
    return () => timeout && clearTimeout(timeout);
  }, [location, error, lastResponse]);

  const map = loading ? (
    <Loading />
  ) : (
    <GoogleMap
      ref={mapRef}
      error={error}
      alwaysPan
      criticalError={
        isNumberNullOrUndefined(location.bearing) ||
        isNumberNullOrUndefined(location.lat) ||
        isNumberNullOrUndefined(location.lng)
      }
      defaultMapType="hybrid"
      defaultZoomLevel={16}
      {...getMapControls({
        location: location?.geolocation,
        driverFullName: location?.driverName,
        drivingSpeed: location?.speed,
        drivingSpeedUnit: !!vehicle?.odometer
          ? vehicle.odometer === Odometer.Miles
            ? "mph"
            : "kph"
          : undefined,
        trailers: location?.trailers,
        theme,
        translate,
      })}>
      {location.lat && location.lng && (
        <GoogleMapMarker
          vehicleName={vehicle?.name ?? ""}
          lat={location.lat}
          lng={location.lng}
          bearing={location.bearing ?? 0}
        />
      )}
    </GoogleMap>
  );

  const isAuthenticated = isNumber(id);
  return (
    <Fragment key={version}>
      <Title
        title={
          <LocationLiveTrackTitle
            vehicle={vehicle}
            isAnonymous={!isAuthenticated}
          />
        }
      />
      {isNumber(id) ? (
        <AuthenticatedLocationLiveTrack
          vehicleId={id}
          handleLocationUpdated={handleLocationUpdated}
          setError={setError}
          setLoading={setLoading}
          setVehicle={setVehicle}>
          {map}
        </AuthenticatedLocationLiveTrack>
      ) : (
        <AnonymousLocationLiveTrack
          vehicleKey={id}
          tenantId={tenantId}
          handleLocationUpdated={handleLocationUpdated}
          setError={setError}
          setLoading={setLoading}
          setVehicle={setVehicle}>
          {map}
        </AnonymousLocationLiveTrack>
      )}
    </Fragment>
  );
};
